import user from '@/services/Auth/authInstance';
import BaseApi from './BaseApi';
import { LOGGING } from '@/config';
import { makeLogger } from '@/services/utils/logger';

const DEBUG_LOG = false; // локальный рубильник файла
const log = makeLogger('ApiService', DEBUG_LOG, LOGGING.NET_SERVICES_LOG);

export default class ApiService extends BaseApi {
  constructor(resourcePath, Model) {
    super(`/${resourcePath}`);
    this.resourcePath = resourcePath;
    this.Model = Model;
  }

  /**
   * Получает все записи ресурса, исключая удалённые.
   * @returns {Promise<Array<Object>>}
   */
  async getAll() {
    try {
      const res = await this.api.get('/', {
        params: {
          filters: { deleted: { $ne: true } }
        }
      });
      const entries = res.data?.data || [];

      return entries
        .map(entry => {
          try {
            if (!entry || typeof entry !== 'object' || typeof entry.attributes !== 'object') {
              log.error(`❌ Некорректный формат entry для ${this.resourcePath}:`, entry);
              return null;
            }
            const obj = this.deserialize(entry);
            return obj;
          } catch (e) {
            log.error(`❌ Ошибка десериализации для ${this.resourcePath}:`, e.message, entry);
            return null;
          }
        })
        .filter(item => item !== null);
    } catch (e) {
      log.error(`❌ Ошибка при получении всех ${this.resourcePath}:`, e);
      return [];
    }
  }

  /**
   * Получает запись по documentId, исключая удалённые.
   * @param {string} id - documentId объекта
   * @returns {Promise<Object|null>}
   */
  async getById(id) {
    try {
      const res = await this.api.get('/', {
        params: {
          filters: {
            documentId: { $eq: id },
            deleted: { $ne: true }
          }
        }
      });
      const raw = res.data?.data;
      const entry = Array.isArray(raw) ? raw[0] : raw;
      log('getById', 'entry=', entry);

      if (!entry) {
        return null;
      }

      const obj = this.deserialize(entry);
      return obj;
    } catch (e) {
      log.error(`❌ Ошибка при получении ${this.resourcePath}/${id}:`, e);
      return null;
    }
  }

  /**
   * Мягко удаляет запись по id (помечает как deleted).
   * @param {string} id - идентификатор объекта
   * @returns {Promise<{id: string|null, success: boolean, error?: Error}>}
   */
  async softDeleteById(id) {
    log('🗑️ ApiService softDeleteById на удаление', id);
    if (!id) {
      log.error('❌ Невозможно выполнить softDeleteById: нет Id');
      return { id: null, success: false, error: new Error('Missing Id') };
    }

    try {
      const res = await this.api.put(`/${id}`, {
        data: { deleted: true }
      });
      log(`🗑 Объект ${this.resourcePath} (${id}) помечен как удалённый`);
      return { id, success: true };
    } catch (e) {
      log.error(`❌ Ошибка при softDeleteById ${this.resourcePath}/${id}:`, e);
      return { id, success: false, error: e };
    }
  }

  /**
   * Сохраняет объект: обновляет, если есть id, иначе создает новый.
   * @param {Object} instance - объект для сохранения
   * @returns {Promise<Object>}
   */
  async save(instance) {
    if (instance.id) {
      return await this.update(instance);
    } else {
      const result = await this.create(instance);
      instance.id = result.id;
      return result;
    }
  }

  /**
   * Создает новый объект через API.
   * @param {Object} instance - объект для создания
   * @returns {Promise<Object>}
   */
  async create(instance) {
    try {
      log('create', `🌐 [api] Создается объект ${this.resourcePath} через API:`, instance);
      const data = this.serialize(instance);
      data.createdByUser = user.dbId;
      data.updatedByUser = user.dbId;
      log('create', '🗃 [api] Сериализованные данные:', data);
      const res = await this.api.post('/', { data });
      log('create', `🟰 [api] Результат:`, res);
      return this.deserialize(res.data?.data);
    } catch (e) {
      // Ошибка критична — не оборачиваем
      log.error('create', `❌ [api] Ошибка при создании ${this.resourcePath}:`, e);
      throw e; // или можно вернуть null, если ты хочешь "мягкое падение"
    }
  }

  /**
   * Обновляет существующий объект через API.
   * @param {Object} instance - объект для обновления
   * @returns {Promise<Object>}
   */
  async update(instance) {
    try {
      log('update', `🌐 [api] Обновляется объект ${this.resourcePath} через API:`, instance);
      const data = this.serialize(instance);
      data.updatedByUser = user.dbId;
      log('update', '🗃 [api] Сериализованные данные:', data);
      const res = await this.api.put(`/${instance.id}`, { data });
      return this.deserialize(res.data?.data);
    } catch (e) {
      // Ошибка критична — не оборачиваем
      log.error(`❌ [api] Ошибка при обновлении ${this.resourcePath}:`, e);
      throw e;
    }
  }

  /**
   * Логическое удаление объекта по id (физическое удаление отключено).
   * @param {string} id - идентификатор объекта
   */
  async deleteById(id) {
    // Игнорируем удаление при использовании API
    log.warn(`⚠️ deleteById(${id}) вызван, но удаление отключено для ${this.resourcePath}`);
  }

  /**
   * Сериализует объект для отправки на сервер.
   * @param {Object} instance - объект для сериализации
   * @returns {Object}
   */
  serialize(instance) {
    const data = { ...instance };
    delete data.id;
    return data;
  }

  /**
   * Десериализует данные, полученные от сервера, в объект модели.
   * @param {Object} entry - данные от сервера
   * @returns {Object}
   */
  deserialize(entry) {
    const attributes = entry?.attributes ?? entry ?? {};
    const id = entry?.documentId ?? entry?.id ?? null;
    const resultObj = {
      ...attributes,
      id,
    };

    return this.Model.fromObject(resultObj);
  }

  /**
   * Приводит коллекцию Strapi к массиву: поддерживает как плоский массив, так и форму { data: [...] }.
   * @param {any} wrapper
   * @returns {any[]}
   */
  asArray(wrapper) {
    if (Array.isArray(wrapper?.data)) return wrapper.data;
    if (Array.isArray(wrapper)) return wrapper;
    return [];
  }
}
